import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Bot, ExternalLink, MessageSquare, PenLine, Search, ThumbsDown, ThumbsUp } from 'lucide-react';
import axiosInstance from '../utils/axiosInstance';
import { timeAgo } from '../components/bible/rich';
import V2Shell from './V2Shell';
import FeedbackTrend from './TrendChart';

const API = 'api/replai/v2/feedback/';
const GORGIAS = 'https://lamptwist.gorgias.com/app/ticket/';
const SOURCE = { gorgias: 'Gorgias', slack: 'Slack', app: 'Replai app' };
const STATUS = {
  fixed: 'Changed in Replai', bible: 'Bible updated', pending: 'Waiting for an answer or approval',
  no_change: 'No change needed', handled: 'Handled',
};
const isDone = (h) => !!(h && h.status && h.status !== 'pending');
// Who rates Replai's notes, next to how many notes they answered (Geoff, 2026-10-23).
const Raters = ({ rows, days }) => (
  <section className="mt-6 rounded-lg border border-[var(--line)] bg-[var(--surface)] p-5">
    <h2 className="text-[1.2rem] font-semibold">Who rates</h2>
    <p className="mt-1 text-sm text-[var(--ink-3)]">
      Ratings given in the last {days === 1 ? 'day' : `${days} days`}, next to the Replai notes each person answered in that time.
      Rated share = ratings ÷ notes answered (a rating can also be on a colleague's note).
      Sent as is = customer drafts sent unchanged without a rating (Send button or pasted), a phantom 👍; edited = sent after changes.
    </p>
    <div className="mt-3 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-xs text-[var(--ink-3)]"><tr>
          <th className="py-1.5 pr-4 font-medium">Person</th><th className="py-1.5 pr-4 text-right font-medium">👍</th>
          <th className="py-1.5 pr-4 text-right font-medium">👎</th>
          <th className="py-1.5 pr-4 text-right font-medium" title="Phantom 👍: sent unchanged, no rating">Sent as is</th>
          <th className="py-1.5 pr-4 text-right font-medium">Edited</th><th className="py-1.5 pr-4 text-right font-medium">With comment</th>
          <th className="py-1.5 pr-4 text-right font-medium">Notes answered</th><th className="py-1.5 pr-4 text-right font-medium">Rated share</th>
          <th className="py-1.5 font-medium">Last rating</th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.name} className={`border-t border-[var(--line-2)] ${r.unknown ? 'text-[var(--ink-3)]' : ''}`}>
            <td className="py-1.5 pr-4 font-medium">{r.name}</td>
            <td className="num py-1.5 pr-4 text-right">{r.up}</td>
            <td className="num py-1.5 pr-4 text-right">{r.down}</td>
            <td className="num py-1.5 pr-4 text-right">{r.phantom_up || 0}</td>
            <td className="num py-1.5 pr-4 text-right">{r.edited || 0}</td>
            <td className="num py-1.5 pr-4 text-right">{r.comments}</td>
            <td className="num py-1.5 pr-4 text-right">{r.unknown ? '–' : r.answered}</td>
            <td className={`num py-1.5 pr-4 text-right font-semibold ${r.share == null ? '' : r.share < 20 ? 'text-amber-700' : 'text-green-700'}`}>
              {r.share == null ? '–' : `${r.share}%`}</td>
            <td className="py-1.5 text-[var(--ink-3)]">{r.last ? timeAgo(r.last) : 'never'}</td>
          </tr>))}</tbody>
      </table>
    </div>
  </section>
);

const needsWork = (r) => r.verdict === 'incorrect' || !!r.note;
const label = (c) => ((c || '').startsWith('noise') ? 'Not a customer' : (c || 'unknown').replace(/_/g, ' '));

const Chip = ({ active, onClick, children }) => (
  <button type="button" onClick={onClick}
    className={`rounded-md px-3 py-1.5 text-sm ${active ? 'bg-[var(--ink)] text-white' : 'text-[var(--ink-2)] hover:bg-[var(--line-2)]'}`}>
    {children}
  </button>
);

const Stat = ({ value, label: l, tone }) => (
  <div className="rounded-lg border border-[var(--line)] bg-[var(--surface)] px-5 py-4">
    <div className={`num text-[1.8rem] font-semibold leading-none ${tone || ''}`}>{value ?? '-'}</div>
    <div className="mt-1.5 text-sm text-[var(--ink-2)]">{l}</div>
  </div>
);

const Handled = ({ r, onChange }) => {
  const [busy, setBusy] = useState(false);
  const h = r.handled;
  const toggle = () => {
    setBusy(true);
    axiosInstance.post(`api/replai/v2/runs/${r.run}/handled/`, { status: isDone(h) ? null : 'handled' })
      .then(({ data }) => onChange(data.handled)).finally(() => setBusy(false));
  };
  return (
    <label className={`flex shrink-0 cursor-pointer select-none items-center gap-2 rounded-md border px-2.5 py-1 text-sm ${isDone(h) ? 'border-green-200 bg-green-50 text-green-800' : 'border-[var(--line)] text-[var(--ink-2)]'}`}
      title={h ? `${STATUS[h.status] || h.status} · ${h.by}${h.at ? `, ${timeAgo(h.at)}` : ''}` : 'Tick when this feedback has been handled'}>
      <input type="checkbox" checked={isDone(h)} disabled={busy} onChange={toggle} className="h-4 w-4 accent-green-700" />
      {isDone(h) ? 'Handled' : h?.status === 'pending' ? 'Waiting' : 'To handle'}
    </label>
  );
};

const Rating = ({ r: initial }) => {
  const [open, setOpen] = useState(false);
  const [r, setR] = useState(initial);
  useEffect(() => setR(initial), [initial]);
  const good = r.verdict === 'correct';
  const h = r.handled;
  return (
    <li className="flex gap-4 rounded-lg border border-[var(--line)] bg-[var(--surface)] p-4">
      <div className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${good ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}
        title={good ? 'Rated right' : 'Rated wrong'}>
        {good ? <ThumbsUp size={17} /> : <ThumbsDown size={17} />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-3">
          <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <Link to={`/tickets/${r.run}`} className="font-semibold hover:underline">R-{r.run}</Link>
            <span className="truncate font-medium">{r.subject || 'No subject'}</span>
            <span className="text-sm text-[var(--ink-3)]">{r.customer}</span>
          </div>
          {(needsWork(r) || h) && <Handled r={r} onChange={(nh) => setR({ ...r, handled: nh })} />}
        </div>
        <div className="mt-0.5 text-sm text-[var(--ink-3)]">
          <span className="first-letter:uppercase">{label(r.category)}</span>
          {' · '}{r.audience === 'agent' ? 'note for an agent' : 'customer reply'}
          {' · '}{SOURCE[r.source] || r.source}{r.by ? ` by ${r.by}` : r.source === 'gorgias' ? ' (no name, older note)' : ''}
          {' · '}{timeAgo(r.at)}
          {r.replaced && ' · note since replaced'}
        </div>
        {r.note && (
          <p className="mt-2 rounded-md bg-[var(--paper)] px-3 py-2 text-[0.95rem] leading-relaxed">
            <MessageSquare size={14} className="mr-1.5 inline-block align-[-2px] text-[var(--ink-3)]" />{r.note}
          </p>
        )}
        {h && h.reply && (
          <div className="mt-2 rounded-md border border-green-200 bg-green-50/60 px-3 py-2 text-[0.95rem] leading-relaxed">
            <div className="mb-0.5 flex items-center gap-1.5 text-sm font-medium text-green-800">
              <Bot size={14} /> {STATUS[h.status] || 'Handled'}
              <span className="font-normal text-[var(--ink-3)]">· {h.by}{h.at ? `, ${timeAgo(h.at)}` : ''}</span>
            </div>
            {h.reply}
          </div>
        )}
        <div className="mt-2 flex flex-wrap gap-4 text-sm">
          <button type="button" onClick={() => setOpen(!open)} className="text-[var(--ink-2)] hover:text-[var(--ink)]">
            {open ? 'Hide the draft' : 'Show the draft'}
          </button>
          <Link to={`/tickets/${r.run}`} className="text-[var(--ink-2)] hover:text-[var(--ink)]">Open in Replai</Link>
          <a href={`${GORGIAS}${r.ticket}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[var(--ink-2)] hover:text-[var(--ink)]">
            Gorgias <ExternalLink size={13} />
          </a>
        </div>
        {open && <pre className="mt-2 whitespace-pre-wrap rounded-md border border-[var(--line)] bg-white p-3 font-sans text-sm leading-relaxed text-[var(--ink-2)]">{r.draft}</pre>}
      </div>
    </li>
  );
};

// A customer draft a colleague changed before sending, next to what went out (Geoff, 2026-10-28).
const Edited = ({ r: initial }) => {
  const [r, setR] = useState(initial);
  useEffect(() => setR(initial), [initial]);
  return (
    <li className="flex gap-4 rounded-lg border border-[var(--line)] bg-[var(--surface)] p-4">
      <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-700" title="Edited before sending">
        <PenLine size={17} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-3">
          <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <Link to={`/tickets/${r.run}`} className="font-semibold hover:underline">R-{r.run}</Link>
            <span className="truncate font-medium">{r.subject || 'No subject'}</span>
            <span className="text-sm text-[var(--ink-3)]">{r.customer}</span>
          </div>
          <Handled r={r} onChange={(nh) => setR({ ...r, handled: nh })} />
        </div>
        <div className="mt-0.5 text-sm text-[var(--ink-3)]">
          <span className="first-letter:uppercase">{label(r.category)}</span>
          {' · '}sent by {r.by}{r.at ? ` · ${timeAgo(r.at)}` : ''}
          {r.similarity != null && ` · ${Math.round(r.similarity * 100)}% the same`}
          {' · '}<a href={`${GORGIAS}${r.ticket}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-[var(--ink)]">Gorgias <ExternalLink size={12} /></a>
        </div>
        <div className="mt-2 grid gap-3 md:grid-cols-2">
          <div>
            <div className="mb-1 text-xs font-medium text-[var(--ink-3)]">Replai's draft</div>
            <pre className="whitespace-pre-wrap rounded-md border border-[var(--line)] bg-white p-3 font-sans text-sm leading-relaxed text-[var(--ink-2)]">{r.draft}</pre>
          </div>
          <div>
            <div className="mb-1 text-xs font-medium text-[var(--ink-3)]">What was sent (start)</div>
            <pre className="whitespace-pre-wrap rounded-md border border-[var(--line)] bg-white p-3 font-sans text-sm leading-relaxed text-[var(--ink-2)]">{r.sent}</pre>
          </div>
        </div>
      </div>
    </li>
  );
};

const BibleChange = ({ b }) => (
  <li className="flex gap-4 rounded-lg border border-[var(--line)] bg-[var(--surface)] p-4">
    <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--paper)] text-[var(--brass)]"><BookOpen size={17} /></div>
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <Link to={`/bible/${b.chapter}`} className="font-semibold hover:underline">{b.chapter_title}</Link>
        <span className="text-sm text-[var(--ink-3)]">{b.kind} {b.block}, v{b.version}, {b.action}</span>
      </div>
      <div className="mt-0.5 text-sm text-[var(--ink-3)]">{b.by} · {timeAgo(b.at)}</div>
      {b.note && <p className="mt-2 text-[0.95rem]">{b.note}</p>}
      <p className="mt-1.5 line-clamp-3 text-sm text-[var(--ink-2)]">{b.text}</p>
    </div>
  </li>
);

const FeedbackV2 = () => {
  // A link can open one rating: /feedback?q=R-11729&days=90
  const link = new URLSearchParams(window.location.search);
  const [days, setDays] = useState(Number(link.get('days')) || 7);
  const [verdict, setVerdict] = useState('');
  const [source, setSource] = useState('');
  const [state, setState] = useState('');
  const [q, setQ] = useState(link.get('q') || '');
  const [tab, setTab] = useState('ratings');
  const [d, setD] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = () => axiosInstance.get(API, { params: { days } })
      .then(({ data }) => alive && setD(data)).catch(() => alive && setD(false));
    load();
    const t = setInterval(load, 60000); // new feedback shows up without a reload
    return () => { alive = false; clearInterval(t); };
  }, [days]);

  const rows = useMemo(() => {
    if (!d) return [];
    const needle = q.trim().toLowerCase();
    return d.rows.filter((r) => (!verdict || r.verdict === verdict) && (!source || r.source === source)
      && (!state || (state === 'done' ? isDone(r.handled) : needsWork(r) && !isDone(r.handled)))
      && (!needle || [r.note, r.subject, r.customer, r.by, r.category, `r-${r.run}`].join(' ').toLowerCase().includes(needle)));
  }, [d, verdict, source, state, q]);
  const c = (d && d.counts) || {};
  const total = (c['verdict:correct'] || 0) + (c['verdict:incorrect'] || 0);

  return (
    <V2Shell>
      <div className="px-6 py-10 sm:px-10 xl:px-14">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-[2.4rem] font-semibold leading-[1.1]">Feedback</h1>
            <p className="prose-body mt-2 text-[var(--ink-2)]">Every rating and comment on Replai's notes, from Gorgias, Slack and this app, newest first. Updates every minute.</p>
          </div>
          <div className="flex gap-1">
            {[1, 7, 30].map((n) => <Chip key={n} active={days === n} onClick={() => setDays(n)}>{n === 1 ? 'Today' : `${n} days`}</Chip>)}
          </div>
        </div>

        {d === false && <p className="mt-8 text-[var(--ink-2)]">Feedback could not be loaded. Reload the page to try again.</p>}
        {d == null && <div className="mt-8 h-64 animate-pulse rounded-lg bg-[var(--line-2)]" />}
        {d && (
          <>
            <section className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
              <Stat value={total} label="ratings" />
              <Stat value={total ? `${Math.round(((c['verdict:correct'] || 0) / total) * 100)}%` : '-'} label="rated right" tone="text-green-700" />
              <Stat value={c['verdict:incorrect'] || 0} label="rated wrong" tone={(c['verdict:incorrect'] || 0) ? 'text-red-700' : ''} />
              <Stat value={c.to_handle || 0} label={`to handle · ${c.handled || 0} handled`} tone={(c.to_handle || 0) ? 'text-amber-700' : ''} />
            </section>

            {(d.raters || []).length > 0 && <Raters rows={d.raters} days={days} />}

            <section className="mt-6">
              <h2 className="mb-2 text-[1.2rem] font-semibold">Trend</h2>
              <FeedbackTrend days={60} />
            </section>

            <div className="mt-8 flex flex-wrap items-center gap-2 border-b border-[var(--line)] pb-3">
              <Chip active={tab === 'ratings'} onClick={() => setTab('ratings')}>Ratings ({total})</Chip>
              <Chip active={tab === 'edited'} onClick={() => setTab('edited')}>Edited before sending ({(d.edited || []).length})</Chip>
              <Chip active={tab === 'bible'} onClick={() => setTab('bible')}>Bible changes ({d.bible.length})</Chip>
              {tab === 'ratings' && (
                <>
                  <span className="mx-2 h-5 w-px bg-[var(--line)]" />
                  <Chip active={!verdict} onClick={() => setVerdict('')}>All</Chip>
                  <Chip active={verdict === 'incorrect'} onClick={() => setVerdict('incorrect')}>👎 Wrong</Chip>
                  <Chip active={verdict === 'correct'} onClick={() => setVerdict('correct')}>👍 Right</Chip>
                  <span className="mx-2 h-5 w-px bg-[var(--line)]" />
                  <Chip active={!state} onClick={() => setState('')}>Any status</Chip>
                  <Chip active={state === 'open'} onClick={() => setState('open')}>To handle</Chip>
                  <Chip active={state === 'done'} onClick={() => setState('done')}>Handled</Chip>
                  <span className="mx-2 h-5 w-px bg-[var(--line)]" />
                  {['', 'gorgias', 'slack', 'app'].map((s) => (
                    <Chip key={s || 'all'} active={source === s} onClick={() => setSource(s)}>{s ? SOURCE[s] : 'Everywhere'}</Chip>
                  ))}
                  <label className="ml-auto flex items-center gap-2 rounded-md border border-[var(--line)] bg-[var(--surface)] px-2.5 py-1.5">
                    <Search size={15} className="text-[var(--ink-3)]" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search comments, R-number, customer"
                      className="w-64 bg-transparent text-sm outline-none" />
                  </label>
                </>
              )}
            </div>

            {tab === 'ratings' ? (
              rows.length ? <ul className="mt-5 space-y-3">{rows.map((r) => <Rating key={`${r.run}-${r.at}`} r={r} />)}</ul>
                : <p className="mt-6 text-[var(--ink-2)]">No feedback matches these filters.</p>
            ) : tab === 'edited' ? (
              (d.edited || []).length ? <ul className="mt-5 space-y-3">{d.edited.map((r) => <Edited key={r.run} r={r} />)}</ul>
                : <p className="mt-6 text-[var(--ink-2)]">No edited drafts in this period.</p>
            ) : (
              d.bible.length ? <ul className="mt-5 space-y-3">{d.bible.map((b, i) => <BibleChange key={`${b.block}-${b.version}-${i}`} b={b} />)}</ul>
                : <p className="mt-6 text-[var(--ink-2)]">No Bible changes in this period.</p>
            )}
          </>
        )}
      </div>
    </V2Shell>
  );
};

export default FeedbackV2;
